/**
 * AutoSyncMultimodalModal
 * ───────────────────────
 * The "Auto Sync" popup. Captures (or accepts an upload of):
 *   • One audio recording of the lecture
 *   • 1-N blackboard / whiteboard photos
 * Hits POST /api/teacher/auto-sync/multimodal, which queues the pipeline
 * server-side, then closes immediately. The new Deskey Session card shows
 * "Processing" while transcription + board OCR run; the teacher then opens
 * the session's review page and clicks "Generate Session" to build the draft.
 *
 * Designed to be a focused, single-shot multimodal capture surface —
 * works on both desktop (file picker) and mobile (camera + voice memo).
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Mic, X, Loader2, ImageIcon, Sparkles, AlertTriangle, CheckCircle2, Trash2, Upload, Square, BookOpen, Library, FileText } from 'lucide-react';
import { toast } from 'sonner';
import TeacherHubDocPicker from './TeacherHubDocPicker';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtSec(n) {
  if (!Number.isFinite(n)) return '0:00';
  const s = Math.max(0, Math.floor(n));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function fmtMB(b) { return `${(b / 1024 / 1024).toFixed(1)} MB`; }

export default function AutoSyncMultimodalModal({ open, onClose, onQueued }) {
  const [audioFile, setAudioFile] = useState(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [boards, setBoards] = useState([]); // [{ file, url }]
  const [titleHint, setTitleHint] = useState('');
  const [referencePdf, setReferencePdf] = useState(null); // File
  const [referenceHubDoc, setReferenceHubDoc] = useState(null); // { id, title, file_ext }
  const [hubPickerOpen, setHubPickerOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSec, setRecordSec] = useState(0);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const audioFileRef = useRef(null);
  const boardFileRef = useRef(null);
  const referenceFileRef = useRef(null);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);
  const recIntervalRef = useRef(null);

  // Cleanup blob URLs on close / unmount
  useEffect(() => {
    return () => {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
      boards.forEach(b => b.url && URL.revokeObjectURL(b.url));
      if (recIntervalRef.current) clearInterval(recIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  // ── Recording ────────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecRef.current = mr;
      recChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(recChunksRef.current, { type: 'audio/webm' });
        const f = new File([blob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
        if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
        setAudioFile(f);
        setAudioBlobUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (recIntervalRef.current) { clearInterval(recIntervalRef.current); recIntervalRef.current = null; }
      };
      mr.start();
      setRecording(true);
      setRecordSec(0);
      recIntervalRef.current = setInterval(() => setRecordSec(s => s + 1), 1000);
    } catch (e) {
      toast.error('Microphone access denied. Use Upload instead.');
    }
  };

  const stopRecording = () => {
    try { mediaRecRef.current?.stop(); } catch (_e) { /* ignore */ }
    setRecording(false);
  };

  // ── Add boards ───────────────────────────────────────────────────────
  const onBoardFiles = (filesList) => {
    const files = Array.from(filesList || []);
    const next = [...boards];
    for (const f of files) {
      if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
        toast.error(`Skipped ${f.name} — only JPEG/PNG/WEBP allowed.`);
        continue;
      }
      if (f.size > 100 * 1024 * 1024) {
        toast.error(`Skipped ${f.name} — over 100 MB.`);
        continue;
      }
      next.push({ file: f, url: URL.createObjectURL(f) });
      if (next.length >= 20) break;
    }
    setBoards(next);
  };

  const removeBoard = (i) => {
    setBoards(prev => {
      const copy = [...prev];
      if (copy[i]?.url) URL.revokeObjectURL(copy[i].url);
      copy.splice(i, 1);
      return copy;
    });
  };

  // ── Generate ─────────────────────────────────────────────────────────
  const generate = async () => {
    if (!audioFile) { toast.error('Add or record an audio file first.'); return; }
    setBusy(true);
    setErrorMsg(null);
    const fd = new FormData();
    fd.append('audio', audioFile);
    boards.forEach(b => fd.append('boards', b.file));
    if (titleHint.trim()) fd.append('title_hint', titleHint.trim());
    if (referencePdf) fd.append('reference_pdf', referencePdf);
    else if (referenceHubDoc) fd.append('reference_hub_doc_id', referenceHubDoc.id);

    try {
      const token = localStorage.getItem('desky_token') || '';
      const res = await fetch(`${API}/api/teacher/auto-sync/multimodal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Upload failed.');
      }
      const d = await res.json();
      // Pipeline continues server-side; the session card tracks progress.
      // Once transcription + board OCR finish, the teacher reviews the
      // extracted text and clicks "Generate Session" to build the draft.
      toast.success('Processing started — open the session from Deskey Sessions to review & generate.');
      if (onQueued) onQueued(d.session_id);
      // Reset for the next capture (component stays mounted while hidden).
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
      boards.forEach(b => b.url && URL.revokeObjectURL(b.url));
      setAudioFile(null); setAudioBlobUrl(null); setBoards([]); setTitleHint('');
      setReferencePdf(null); setReferenceHubDoc(null);
      setBusy(false);
      onClose();
    } catch (e) {
      setErrorMsg(e.message || 'Failed.');
      setBusy(false);
    }
  };

  // Portalled to document.body -- this component renders deep inside the
  // dashboard's scrollable content column (overflow-y-auto), which clips
  // ANY descendant to its own box regardless of position:fixed. Without
  // the portal, the backdrop only covers that column, visibly excluding
  // the sidebar and header strip above/beside it.
  return createPortal((
    <div className="fixed inset-0 z-[140] flex items-end md:items-center justify-center" data-testid="auto-sync-modal" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)' }}>
      <div
        className="bg-white w-full md:w-[min(900px,94vw)] md:max-h-[88vh] rounded-t-2xl md:rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Auto Sync · Multimodal</div>
            <div className="text-[16px] font-extrabold text-slate-900">Voice + Board fusion</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center" data-testid="auto-sync-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <div className="p-5 grid gap-5">
              {/* Title hint */}
              <input
                type="text"
                value={titleHint}
                onChange={e => setTitleHint(e.target.value)}
                placeholder="Title"
                className="w-full h-11 px-4 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white text-sm focus:outline-none focus:border-emerald-400 transition-all"
                data-testid="auto-sync-title-hint"
              />

              {/* AUDIO BLOCK */}
              <div className="rounded-2xl border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5">
                    <Mic className="w-3.5 h-3.5" /> 1 · Lecture audio
                  </div>
                  {audioFile && <span className="text-[10px] text-emerald-600 font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {audioFile.name} · {fmtMB(audioFile.size)}</span>}
                </div>
                {audioBlobUrl && (
                  <audio controls src={audioBlobUrl} className="w-full h-9 mb-2" data-testid="auto-sync-audio-preview" />
                )}
                <div className="flex flex-wrap gap-2">
                  {!recording ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      disabled={busy}
                      data-testid="auto-sync-start-record"
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[12px] font-bold disabled:opacity-50">
                      <Mic className="w-3.5 h-3.5" /> Record
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={stopRecording}
                      data-testid="auto-sync-stop-record"
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-zinc-900 text-white text-[12px] font-bold animate-pulse">
                      <Square className="w-3.5 h-3.5" /> Stop · {fmtSec(recordSec)}
                    </button>
                  )}
                  <input
                    ref={audioFileRef}
                    type="file"
                    accept=".mp3,.m4a,.wav,.webm,.ogg,.flac,.aac,.mp4,audio/*"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
                      setAudioFile(f);
                      setAudioBlobUrl(URL.createObjectURL(f));
                    }}
                    data-testid="auto-sync-audio-file"
                  />
                  <button
                    type="button"
                    onClick={() => audioFileRef.current?.click()}
                    disabled={busy || recording}
                    data-testid="auto-sync-upload-audio"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 text-[12px] font-bold disabled:opacity-50">
                    <Upload className="w-3.5 h-3.5" /> Upload audio
                  </button>
                </div>
              </div>

              {/* BOARDS BLOCK */}
              <div className="rounded-2xl border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" /> 2 · Board photos · {boards.length}/20
                  </div>
                  <input
                    ref={boardFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    capture="environment"
                    className="hidden"
                    onChange={e => { onBoardFiles(e.target.files); e.target.value = ''; }}
                    data-testid="auto-sync-board-files"
                  />
                  <button
                    type="button"
                    onClick={() => boardFileRef.current?.click()}
                    disabled={busy || boards.length >= 20}
                    data-testid="auto-sync-add-board"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-bold disabled:opacity-50">
                    <Camera className="w-3.5 h-3.5" /> Add boards
                  </button>
                </div>
                {boards.length === 0 ? (
                  <div
                    className="rounded-xl border-2 border-dashed border-zinc-200 py-6 text-center text-[12px] text-slate-500 cursor-pointer hover:bg-zinc-50"
                    onClick={() => boardFileRef.current?.click()}
                    data-testid="auto-sync-board-empty">
                    Tap to snap or pick blackboard / whiteboard photos. JPEG / PNG / WEBP, ≤100 MB each.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {boards.map((b, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden border border-zinc-200 aspect-square" data-testid={`auto-sync-board-${i}`}>
                        <img src={b.url} alt={`board-${i}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeBoard(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          data-testid={`auto-sync-board-remove-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] font-bold p-1">
                          Board {i + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* REFERENCE MATERIAL BLOCK */}
              <div className="rounded-2xl border border-zinc-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5" /> 3 · Reference material (optional)
                  </div>
                </div>
                {!referencePdf && !referenceHubDoc ? (
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={referenceFileRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        setReferenceHubDoc(null);
                        setReferencePdf(f);
                      }}
                      data-testid="auto-sync-reference-file"
                    />
                    <button
                      type="button"
                      onClick={() => referenceFileRef.current?.click()}
                      disabled={busy}
                      data-testid="auto-sync-upload-reference"
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 text-[12px] font-bold disabled:opacity-50">
                      <Upload className="w-3.5 h-3.5" /> Upload PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => setHubPickerOpen(true)}
                      disabled={busy}
                      data-testid="auto-sync-tag-hub-doc"
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 text-[12px] font-bold disabled:opacity-50">
                      <Library className="w-3.5 h-3.5" /> Tag from DeskeyHUB
                    </button>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 pl-3 pr-1.5 h-9 rounded-xl bg-emerald-50 border border-emerald-200 text-[12px] font-bold text-emerald-800" data-testid="auto-sync-reference-chip">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="max-w-[220px] truncate">{referencePdf ? referencePdf.name : referenceHubDoc.title}</span>
                    <button
                      type="button"
                      onClick={() => { setReferencePdf(null); setReferenceHubDoc(null); }}
                      disabled={busy}
                      data-testid="auto-sync-reference-remove"
                      className="w-6 h-6 rounded-full hover:bg-emerald-100 flex items-center justify-center disabled:opacity-50">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">Tag a syllabus or textbook PDF and Deskey will highlight the parts of the lecture drawn straight from it.</p>
              </div>

              {/* Upload progress */}
              {busy && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="auto-sync-progress">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  <div className="text-[13px] font-bold text-emerald-900">Uploading audio & boards…</div>
                </div>
              )}

              {errorMsg && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="auto-sync-error">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <div className="text-[13px] font-bold text-red-900">{errorMsg}</div>
                </div>
              )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 border-t border-zinc-100 flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-zinc-200 text-[12px] font-bold hover:bg-zinc-50" data-testid="auto-sync-cancel">
            Cancel
          </button>
          <button
            onClick={generate}
            disabled={busy || !audioFile}
            data-testid="auto-sync-generate"
            className="h-10 px-5 rounded-xl text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 transition-all hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #34A853 0%, #0d9488 100%)' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? 'Generating…' : 'Generate Smart Note'}
          </button>
        </div>
      </div>
      {hubPickerOpen && (
        <TeacherHubDocPicker
          onClose={() => setHubPickerOpen(false)}
          onSelect={(doc) => { setReferenceHubDoc(doc); setReferencePdf(null); setHubPickerOpen(false); }}
        />
      )}
    </div>
  ), document.body);
}

